import React, { useState, useEffect, useRef, Fragment } from 'react';
import { PostRequest, GetRequest, Tooltip } from 'components/common';
import QuoteBuilderForm from 'components/quote_builder/QuoteBuilderForm.jsx';
// The same property-tag chips the picker cards show for each option, reused here so the edit form
// summarises the selected record's capabilities the same way.
import AttributePropertyTags from 'components/quote_builder/attributePropertyTags.jsx';
import LibraryAttributePicker from 'components/quote_builder/LibraryAttributePicker.jsx';
import { ContentPopup } from 'components/ContentPopup.jsx';
import { ValidatorFactory } from 'components/common';
import { ListenCondition } from 'components/quote_builder/form_fields/index.js'

const UserUnitPriceAttributeField = ({ config, libraryTabConfig, value, setValue, unit, register, errors, disabled, setFetchInProgress, setChildSubFormOpen, options, readOnlyValues, context, requestPublishItemOnChange, item, navSection, values, onAttributesLoaded }) => {
  const VALUE_NEW = "new-attribute";
  const VALUE_NOT_AVAILABLE = 'not-available'
  const VALUE_EMPTY = "";

  const [attributes, setAttributes] = useState([]);
  // Whether the initial attribute list has come back. Until it has, the default-value effect below must not
  // fall back to VALUE_NEW (which opens the create sub-form): an empty list mid-fetch is not "no records",
  // and defaulting to VALUE_NEW then flips back once the records load -- flashing the create sub-form.
  const [attributesLoaded, setAttributesLoaded] = useState(false);
  const [sourceAttributes, setSourceAttributes] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [newAttributeValue, setNewAttributeValue] = useState('');
  const [newAttributesAdded, setAttributesAdded] = useState(false);
  const [newAttributeFormError, setNewAttributeFormError] = useState('');
  // On a library-enabled attribute, "create new" opens the library picker first; this flips to the blank
  // manual form when the user chooses "enter manually" (reset whenever the selection leaves "create new").
  const [manualEntry, setManualEntry] = useState(false);

  const [readOnly, setReadOnly] = useState(false);
  const [defaultValue, setDefaultValue] = useState(config.default_value);
  const [editing, setEditing] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [editNameError, setEditNameError] = useState('');
  const editPopupRef = useRef();
  // context pre-selects the machine the form was opened for, but only ONCE, on the first attribute load.
  // Re-applying it on every reload would fight the user: when they add a new machine from this form ("create
  // new" -> copy from the library re-fetches this list), the just-added machine is the selection they want,
  // and re-applying the original context machine here would snap the dropdown straight back off it.
  const contextAppliedRef = useRef(false);

  const validate = (val) => {
    if(config.validators != undefined) {
      for(let i=0; i< config.validators.length; i++) {
        let validator = config.validators[i];
        if(!ValidatorFactory(validator, value)) {
          setValidationError(t(`quote_builder.validator_${validator}_message`));
          return t(`quote_builder.validator_${validator}_message`);
        }
      }
    }
    setValidationError('');
    return;
  }

  const handleOnChange = (e) => {
    setValue(config.name, e.target.value);
  }

  const materialIdByyMaterialAttribute = (material_attribute) => {
    var name_en = undefined;

    name_en = {
      carton_material_user_price_id: 'Carton',
      hard_cardboard_material_user_price_id: 'Hard cardboard',
      pvc_material_user_price_id: 'PVC',
      corrugated_cardboard_material_user_price_id: 'Corrugated cardboard',
    }[material_attribute];

    if(navSection != undefined && navSection.part_type == 'carton_lamination' && name_en == 'Carton') {
      name_en = 'Carton + Corrugated Cardboard (Lamination)'
    }

    if(name_en == undefined || attributes.length == 0)   { return undefined }

    let attribute = Array.from(attributes).find((attribute) => attribute.name_en == name_en );
    if(attribute == undefined) { return undefined }

    return attribute.id;
  }

  useEffect(() => {
    if(attributes.length == 0) { return; }

    if (readOnlyValues != undefined) {
      readOnlyValues.map((readOnlyValue) => {
        if(readOnlyValue.name == config.name) {

          if(readOnlyValue.value_eval != undefined) {
            let _value = eval(readOnlyValue.value_eval);
            if (_value != undefined && _value != '') {
              setTimeout(() => {
                setValue(config.name, () => { return _value } );
                setReadOnly(readOnlyValue.make_read_only == undefined ? (_prev) => true :  (_prev) => readOnlyValue.make_read_only );
              }, 250)
            }
          }
        }
      })
    }

    if(context != undefined && !contextAppliedRef.current) {
      for (const [contextKey, contextValue] of Object.entries(context)) {
        if(contextKey == config.key) {
          contextAppliedRef.current = true;
          setTimeout(() => {
            setValue(config.name, () => { return contextValue } );
          }, 250);
        }
      }
    }
  }, [attributes])

  const fetchUserUnitPriceAttributes = (callback) => {
    if(disabled) { return; }

    setFetchInProgress(true);

    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/list/${config.key}`,
      callback: (data) => {
        setAttributes(data);
        setAttributesLoaded(true);
        // Report the loaded attributes (with their properties) up so the form can resolve vap()
        // expressions against the selected attribute (see QuoteBuilderForm.registerAttributeProperties).
        if(onAttributesLoaded != undefined) { onAttributesLoaded(data); }
        if(callback != undefined) { callback(); }
        setFetchInProgress(false);
      }
    })
  }

  useEffect(fetchUserUnitPriceAttributes, []);

  useEffect(() => {
    if (!config.filter_attributes_from) { return; }

    const sourceKey = config.filter_attributes_from.replace('user_unit_price_attribute.', '');
    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/list/${sourceKey}`,
      callback: (data) => {
        setSourceAttributes(data);
      }
    });
  }, []);

  const getFilteredAttributes = () => {
    if (!config.filters || !attributes.length) { return attributes; }

    let selectedSourceAttribute = null;
    if (config.filter_attributes_from && sourceAttributes && values) {
      let sourceValue = values[config.filter_attributes_from];
      if (sourceValue && sourceValue !== VALUE_NEW && sourceValue !== VALUE_EMPTY) {
        selectedSourceAttribute = sourceAttributes.find((attribute) => String(attribute.id) === String(sourceValue));
      }
    }

    let data = attributes;
    for (let i = 0; i < config.filters.length; i++) {
      data = data.filter((attribute) => { return eval(config.filters[i].filter); });
    }
    return data;
  };

  const filteredAttributes = getFilteredAttributes();

  useEffect( () => {
    // Wait for the initial fetch: an empty list before it returns is not "no records", and defaulting to
    // VALUE_NEW here (opening the create sub-form) would flash it away once the records arrive.
    if(!attributesLoaded) { return; }
    if(value != '' && value != undefined && value !='new-attribute' ) { return; }

    let valueToSet = attributes.length > 0 ? (config.empty_option_enabled ? VALUE_EMPTY : (config.not_available_enabled ? VALUE_NOT_AVAILABLE : attributes[0].id)) : (config.not_available_enabled ? VALUE_NOT_AVAILABLE : (config.create_enabled ? VALUE_NEW : VALUE_EMPTY))

    if(newAttributesAdded) {
      valueToSet = attributes.length > 0 ? attributes[attributes.length - 1].id : (config.not_available_enabled ? VALUE_NOT_AVAILABLE : (config.create_enabled ? VALUE_NEW : VALUE_EMPTY))
    }

    if(defaultValue != '' && defaultValue != undefined) {
      for(let i=0; i< attributes.length; i++) {
        if (attributes[i].name_en == defaultValue) {
          setValue(config.name, attributes[i].id);
          return;
        }
      }
    }

    setValue(config.name, (prev) => { return valueToSet } );

  }, [attributes, attributesLoaded])

  useEffect(() => {
    if (config.create_enabled && config.create_sub_form != undefined && setChildSubFormOpen) {
      setChildSubFormOpen(value == VALUE_NEW);
    }
    return () => {
      if (setChildSubFormOpen) { setChildSubFormOpen(false); }
    };
  }, [value])

  const saveNewAttributeValue = () => {
    if(newAttributeValue == '') { setNewAttributeFormError('quote_builder.missing_value_error'); return; }
    setFetchInProgress(true);

    PostRequest({
      url: `/quote-builder/user_unit_price_attributes/create/${config.key}`,
      data: {
        name: newAttributeValue,
      },
      callback: (_data) => {
        setAttributesAdded(true);
        setNewAttributeFormError('');
        fetchUserUnitPriceAttributes();
        setValue(config.name, _data.new_attribute_id);
        setNewAttributeValue('');
        setFetchInProgress(false);
      },
      400: (data) => {
        setNewAttributeFormError( 'quote_builder.invalid_value_error' );
        setFetchInProgress(false);
      }
    })
  }

  const closeNewAttributeForm = () => {
    setNewAttributeValue('');
    setValue(config.name, attributes[0]?.id);
  }

  const newAttributeForm = () => {
    return (
      <div className="form-inline newAttributeForm">
        <div className="form-group">
          <div className="input-group">
            <div className="input-group-addon">
              {config.create_input_label != undefined && t(config.create_input_label)}
              {config.create_input_label == undefined && t('quote_builder.new_attribute_with_key').replace('%{key}', t(`quote_builder.${config.key}`).toLowerCase())}
              </div>
              <input
                type="text"
                className={newAttributeFormError != '' ? 'input-error form-control' : 'form-control'}
                size="15"
                value={newAttributeValue}
                onChange={(e) => {setNewAttributeValue(e.target.value)}}
                />
            </div>
          </div>

          <button type="button" className="btn simple-btn" onClick={saveNewAttributeValue} > {t('quote_builder.save')} </button> &nbsp;
          <button className="btn" type="button" onClick={closeNewAttributeForm}> {t('quote_builder.cancel')} </button>

          {newAttributeFormError != '' && <center> <small className="error"> {t(newAttributeFormError)} </small> </center> }
      </div>
    )
  }

  // onBack, when given (the library flow's "enter manually"), makes Cancel return to the library picker instead
  // of refreshing the list -- so the manual form is a step you can back out of, not a dead end.
  const newAttributeSubForm = (onBack) => {
    const afterSave = () => {
      setAttributesAdded(true);
      fetchUserUnitPriceAttributes();
    }
    return (<div>
      <QuoteBuilderForm config={config.create_sub_form} afterSave={afterSave} afterCancel={onBack || afterSave} readOnlyValues={readOnlyValues} context={context} options={options} />
    </div>)
  }

  // "Create new" on a library-enabled attribute opens the curated library (search + copy a machine) with the
  // manual form behind an "enter manually" link -- the same easier flow the machine tab and /mcc offer. entryOnly
  // stops after the copy (no price step here -- this field only needs to REFERENCE the machine); on copy the new
  // machine is selected in the dropdown (fetch the list, then set this field to it).
  const newAttributeLibraryPicker = () => (
    // A framed card around the picker so this "form inside the form" reads as its own island. Only the picker
    // needs it: the manual create form (newAttributeSubForm) is a QuoteBuilderForm table that already frames
    // itself, so a border there would double up.
    <div style={{
      marginTop: '10px', padding: '14px 16px',
      background: 'var(--surface, #ffffff)', border: '1px solid var(--rule, #e3e7ec)',
      borderRadius: 'var(--dct-radius, 8px)', boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
    }}>
      <LibraryAttributePicker
        userUnitPriceKey={config.key}
        tabConfig={libraryTabConfig}
        onCopy={(newAttributeId) => { fetchUserUnitPriceAttributes(() => setValue(config.name, newAttributeId)); }}
        onManualEntry={() => setManualEntry(true)}
        onClose={() => {}}
        hideDone
        entryOnly
      />
    </div>
  )

  // Modal editor for the currently selected attribute, opened from the "edit" link. Attributes that
  // carry a rich create_sub_form (machines, sheet dimensions, ...) are edited with that same form via
  // QuoteBuilderForm's UserUnitPriceAttribute edit mode (fetch /properties/:id, save
  // /update_properties/:id) — exactly what the standalone "Printing and cutting machines" list uses.
  // Name-only attributes (a brand, a type, ...) are edited with a single name field via update_name,
  // mirroring this field's own create flow. Both are shown in a ContentPopup, the same modal
  // UserUnitPrice uses to edit a price.
  const closeEditor = () => {
    setEditing(false);
    setEditNameError('');
    if (editPopupRef.current) { editPopupRef.current.style.display = "none"; }
  }

  const startEditing = (e) => {
    e.preventDefault();
    if (config.create_sub_form == undefined) {
      setEditNameValue(selectedAttribute != undefined ? selectedAttribute.name : '');
      setEditNameError('');
    }
    setEditing(true);
  }

  // The rich edit modal shows the sub-form's edit_title in its header. Several sub-forms only define
  // new_title, which leaves the edit modal header blank. Fall back to this picker's own label (an
  // existing, already-translated key) so the modal is always titled — e.g. editing a layer shows
  // "2nd layer". Sub-forms that do define edit_title (machines, material types, ...) keep it.
  const editSubFormConfig = () => {
    const subForm = config.create_sub_form;
    if (subForm.edit_title != undefined) { return subForm; }
    const fallbackTitle = config.label != undefined ? config.label : `quote_builder.${config.key}`;
    return { ...subForm, edit_title: fallbackTitle };
  }

  const editAttributePopup = () => {
    const afterSave = () => {
      closeEditor();
      // The selected id is unchanged; re-fetch so the dropdown label reflects any edits (renamed
      // machine, changed dimensions, ...).
      fetchUserUnitPriceAttributes();
    }
    // Match the standalone "… machines"/"… types" list editor (SettingsUserUnitPriceAttributeList):
    // it passes NO context/readOnlyValues into the edit form. In edit mode the form fetches and seeds
    // the record's real saved values, and a stray context/readOnlyValues write could clobber them.
    const content = (
      <QuoteBuilderForm config={editSubFormConfig()}
        showingState="edit" editStateId={value} editedKlass="UserUnitPriceAttribute" linkToSettings={false}
        afterSave={afterSave} afterCancel={closeEditor} />
    )
    return <ContentPopup myRef={editPopupRef} autoOpen={true} content={content} cancelButton={false} beforeClose={() => setEditing(false)} revealWhenReady={true} />
  }

  const saveEditedName = () => {
    if (editNameValue == '') { setEditNameError('quote_builder.missing_value_error'); return; }
    setFetchInProgress(true);

    // .catch handles EVERY failure status uniformly (400 invalid value, but also 403 not-editable,
    // 404, 500): clear the in-progress flag and keep the editor open with an error. Handling only 400
    // would leave the parent form stuck in its loading/disabled state on any other status.
    PostRequest({
      url: `/quote-builder/user_unit_price_attributes/update_name/${value}`,
      data: { name: editNameValue },
      callback: (_data) => {
        closeEditor();
        fetchUserUnitPriceAttributes();
      }
    }).catch((_error) => {
      setEditNameError('quote_builder.invalid_value_error');
      setFetchInProgress(false);
    })
  }

  // Name-only attributes get a lightweight inline editor (a single name field), rendered under the
  // dropdown just like this field's own "create" name form — a full modal for one field looks broken.
  // The rich multi-field editor above stays a modal (it has its own title and many fields).
  const editNameForm = () => {
    return (
      <div className="form-inline newAttributeForm" style={{ marginTop: '4px' }}>
        <div className="form-group">
          <div className="input-group">
            <div className="input-group-addon">
              { config.label != undefined ? t(config.label) : defaultLabel() }
            </div>
            <input
              type="text"
              className={editNameError != '' ? 'input-error form-control' : 'form-control'}
              size="25"
              value={editNameValue}
              onChange={(e) => { setEditNameValue(e.target.value) }}
            />
          </div>
        </div>

        <button type="button" className="btn simple-btn" onClick={saveEditedName}> {t('quote_builder.save')} </button> &nbsp;
        <button className="btn" type="button" onClick={closeEditor}> {t('quote_builder.cancel')} </button>

        {editNameError != '' && <center> <small className="error"> {t(editNameError)} </small> </center> }
      </div>
    )
  }

  const defaultLabel = () => {
    return t(`quote_builder.${config.key}`);
  }

  const defaultCreateCta = () => {
    return t(`quote_builder.new_user_unit_price`).replace("%s", t(`quote_builder.${config.key}`))
  }

  const callRequestPublishItemOnChange = () => {
    if(config.publish_item_on_change && config.publish_item_if_eval == undefined) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }

    if(config.publish_item_on_change && config.publish_item_if_eval != undefined && eval(config.publish_item_if_eval) ) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }
  }

  useEffect(() => {
    callRequestPublishItemOnChange();
  }, [value])

  // Close the edit popup whenever the selection changes, so the open editor always matches the
  // currently selected attribute (QuoteBuilderForm captures editStateId at mount) and can never
  // edit a stale id after the user picks a different option.
  useEffect(() => {
    setEditing(false);
  }, [value])

  // Leaving "create new" (picked a machine, copied one, etc.) clears the manual-entry flip, so the next time
  // the user opens "create new" they land on the library picker again rather than the last blank form.
  useEffect(() => {
    if (value !== VALUE_NEW) { setManualEntry(false); }
  }, [value])

  // Mirror a property of the selected attribute into another form field, optionally mapping
  // the raw value (with a default). Example: copy the chosen printing machine's printing_type
  // into pricing_mode, mapped to the pricing basis the calculator understands. Generic and
  // config-driven so it is reusable by any UserUnitPriceAttribute picker.
  useEffect(() => {
    if (config.copy_selected_property == undefined) { return; }
    if (attributes.length == 0) { return; }

    const { property, into, map } = config.copy_selected_property;

    const mapValue = (raw) => {
      if (map == undefined) {
        return raw == undefined ? '' : raw;
      }
      if (raw != undefined && map[raw] != undefined) {
        return map[raw];
      }
      return map.default != undefined ? map.default : (raw == undefined ? '' : raw);
    };

    if (value == VALUE_NEW || value == VALUE_NOT_AVAILABLE || value == VALUE_EMPTY || value == undefined) {
      setValue(into, mapValue(undefined));
      return;
    }

    const selectedAttribute = attributes.find((attribute) => String(attribute.id) === String(value));
    const rawValue = (selectedAttribute && selectedAttribute.properties) ? selectedAttribute.properties[property] : undefined;
    setValue(into, mapValue(rawValue));
  }, [value, attributes])

  const sourceFieldValue = config.filter_attributes_from && values ? values[config.filter_attributes_from] : null;

  useEffect(() => {
    if (!config.filter_attributes_from || !sourceAttributes || filteredAttributes.length === 0) { return; }
    if (value === VALUE_NEW || value === VALUE_EMPTY || value === VALUE_NOT_AVAILABLE) { return; }

    const currentSelectionValid = filteredAttributes.some((attribute) => String(attribute.id) === String(value));
    if (!currentSelectionValid) {
      setValue(config.name, filteredAttributes[0].id);
    }
  }, [sourceFieldValue, sourceAttributes, value, attributes])

  const noOp = () => { }

  if(config.listen_conditions != undefined) {
    for(let i=0; i < config.listen_conditions.length; i++) {

      useEffect(() => {
        let listener = ListenCondition.listener({
          config: config,
          condition: config.listen_conditions[i],
          item: item,
          navSection: navSection,
          setValue: setValue,
          GetRequest: GetRequest,
          setReadOnly: setReadOnly,
          setReadOnlyMessage: noOp,
          setNotAvailable: noOp,
          setNotAvailableReason: noOp,
          options: options,
          fetchUserUnitPrices: noOp,
          fetchSystemUnitPrices: noOp,
        });

        document.addEventListener(config.listen_conditions[i].listen, listener);
        return () => { document.removeEventListener(config.listen_conditions[i].listen, listener);}

      }, []);
    }
  }

  // The "edit" link is offered only for attributes that have their own settings tab (the
  // "Carton material types", "Layers", ... lists) — window.quote_builder_attribute_keys_with_tab is
  // populated only on the settings pages, so this also scopes the affordance to those pages. Beyond
  // that it requires an attribute the user owns and can edit (can_be_edited is false for system and
  // read-only records, so this is exactly "my own, editable attribute") and a real selection (not the
  // create / empty / not-available sentinels). This mirrors the server, which scopes both the rich
  // update (update_properties) and the name update (update_name) to current_user and rejects
  // non-editable records.
  const attributeHasListingTab =
    Array.isArray(window.quote_builder_attribute_keys_with_tab) &&
    window.quote_builder_attribute_keys_with_tab.includes(config.key);
  const selectedAttribute = attributes.find((attribute) => String(attribute.id) === String(value));
  const canEditSelectedAttribute =
    attributeHasListingTab &&
    !readOnly &&
    selectedAttribute != undefined &&
    selectedAttribute.can_be_edited &&
    value != VALUE_NEW && value != VALUE_EMPTY && value != VALUE_NOT_AVAILABLE;

  // A field opts into this summary by declaring `listing_properties`; the chips describe the selected
  // record the same way its picker card does. Create / empty / not-available carry no record to summarise.
  const showAttributeTags =
    config.listing_properties != undefined &&
    selectedAttribute != undefined &&
    selectedAttribute.properties != undefined &&
    value != VALUE_NEW && value != VALUE_EMPTY && value != VALUE_NOT_AVAILABLE;

  return (
    <Fragment>
    <tr>
      <td>
        { config.label != undefined && t(config.label) }
        { config.label == undefined && defaultLabel() }
        { config.tooltip_text != undefined && <Tooltip title={t(config.tooltip_text)} /> }
        {config.helper_text != undefined && (
          <><br /><small className="form-text text-muted" dangerouslySetInnerHTML={{ __html: t(config.helper_text) }} /></>
        )}
      </td>
      <td align="right">
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: '265px' }}>
        <select
          name={config.name}
          value={value}
          disabled={readOnly}
          style={{ flex: '1 1 auto', minWidth: 0 }}
          className={`form-control general-frm-element ${validationError != '' ? 'input-error' : ''}`}
          {...register(config.name.replace('.', '_'), {
          validate: validate,
          onChange: handleOnChange
        })}
         >
        {config.empty_option_enabled && <optgroup label="" ><option key={VALUE_EMPTY} value={VALUE_EMPTY}> </option> </optgroup> }
        {config.not_available_enabled && <optgroup label="" ><option key="n/a" value={VALUE_NOT_AVAILABLE}> {t('quote_builder.not_included')} </option> </optgroup> }
        {filteredAttributes.map((attribute) => {
          return <option
            key={attribute.id}
            value={attribute.id}> { window.locale == 'en' ? (attribute.dropdown_label_en || attribute.name_en) : (attribute.dropdown_label_tr || attribute.name_tr) }
          </option>
        })}

        {config.create_enabled && <optgroup label="" ><option key="new_attribute" value={VALUE_NEW}>
        {config.create_cta != undefined && t(config.create_cta)}
        {config.create_cta == undefined && defaultCreateCta() }
        </option> </optgroup> }

      </select>
        { canEditSelectedAttribute && !editing &&
          <a href="#" className="qb-attribute-edit-link" onClick={startEditing} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            <i className="fa fa-pencil-square-o" aria-hidden="true"></i> {t('quote_builder.edit')}
          </a>
        }
        </div>

      {validationError != '' && <small className="error"> {validationError} </small>}
      {config.create_enabled && value == VALUE_NEW && config.create_sub_form == undefined && newAttributeForm() }
      {config.create_enabled && value == VALUE_NEW && config.create_sub_form != undefined &&
        ((libraryTabConfig && libraryTabConfig.library_enabled && !manualEntry)
          ? newAttributeLibraryPicker()
          : newAttributeSubForm((libraryTabConfig && libraryTabConfig.library_enabled) ? () => setManualEntry(false) : undefined)) }
      {editing && canEditSelectedAttribute && config.create_sub_form != undefined && editAttributePopup() }
      {editing && canEditSelectedAttribute && config.create_sub_form == undefined && editNameForm() }
      </td>
    </tr>
    { showAttributeTags &&
      <tr>
        <td colSpan={2} style={{ paddingTop: 0 }}>
          <AttributePropertyTags
            properties={selectedAttribute.properties}
            subFormAttributes={config.create_sub_form?.attributes}
            listingProperties={config.listing_properties}
            align="right"
          />
        </td>
      </tr>
    }
    </Fragment>
  )
}

export default UserUnitPriceAttributeField;
